import { ReactNode, useState } from 'react';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { CompanySelector } from './CompanySelector';
import { Button } from '@/components/ui/button';
import { LogOut, User, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/hooks/useAuthStore';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NotificationBell } from '@/components/investor/NotificationBell';
import { HelpMenu } from '@/components/help/HelpMenu';
import { PlatformTour } from '@/components/help/PlatformTour';
import { usePlatformTour } from '@/hooks/usePlatformTour';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DashboardLayoutProps {
  children: ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, signOut } = useAuthStore();
  const { activeTour, runTour, startTour, completeTour } = usePlatformTour();
  const [ellaDialogOpen, setEllaDialogOpen] = useState(false);
  const [ellaQuestion, setEllaQuestion] = useState('');
  const [ellaAnswer, setEllaAnswer] = useState('');
  const [ellaLoading, setEllaLoading] = useState(false);

  const handleAskElla = async () => {
    if (!ellaQuestion.trim()) return;

    setEllaLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('ella-platform-guide', {
        body: { question: ellaQuestion },
      });

      if (error) throw error;
      setEllaAnswer(data.answer);
    } catch (error) {
      console.error('Error asking Ella:', error);
      toast.error('Failed to get answer from Ella');
    } finally {
      setEllaLoading(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar data-tour="sidebar" />
        <div className="flex-1 flex flex-col">
          <header className="h-16 border-b bg-card flex items-center justify-between px-6">
            <div className="flex items-center gap-4" data-tour="company-selector">
              <SidebarTrigger />
              <CompanySelector />
            </div>
            <div className="flex items-center gap-2">
              <NotificationBell />
              <div data-tour="help-menu">
                <HelpMenu
                  onStartTour={startTour}
                  onAskElla={() => {
                    setEllaDialogOpen(true);
                    setEllaQuestion('');
                    setEllaAnswer('');
                  }}
                />
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="flex-1 p-6 overflow-auto">{children}</main>
        </div>
      </div>

      {activeTour && <PlatformTour tourType={activeTour} run={runTour} onComplete={completeTour} />}

      <Dialog open={ellaDialogOpen} onOpenChange={setEllaDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ask Ella - Platform Guide</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">
                Ask me anything about using the platform:
              </label>
              <Textarea
                placeholder="e.g., How do I issue shares? What's a cap table? How does vesting work?"
                value={ellaQuestion}
                onChange={(e) => setEllaQuestion(e.target.value)}
                rows={3}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAskElla();
                  }
                }}
              />
            </div>
            <Button onClick={handleAskElla} disabled={ellaLoading || !ellaQuestion.trim()}>
              {ellaLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Ask Ella
            </Button>

            {ellaAnswer && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="text-sm whitespace-pre-wrap">{ellaAnswer}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}
